<template>
      <div class="filter-container">
        <FilterSuiviFacture
            @search="search"
            @clearFilter="clearFilter"
            @setSociete="setSociete"
            @setEtablissement="setEtablissement"
            @setFournisseur="setFournisseur"
            @setNumeroFacture="setNumeroFacture"
            @setDateFrom="setDateFrom"
            @setDateTo="setDateTo"
            @comparison="setComparison"
            @setMontant="setMontant"
            @setJoursRetard="setJoursRetard"
            :filter="filter"
            :comparisonList="comparisonList"
        />
    </div>
    <div class="list-container">
        <h2 class="title">
            <span v-if="tableData && tableData.length > 0 && !isFilterLoading">
                <template v-if="isFiltered && totalCount > 0">
                    {{ $t('facture.enRetard.filtre', { count: +count }) }} / {{ $t('facture.enRetard.title', { count: +totalCount }) }}
                </template>
                <template v-else>
                    {{ $t('facture.enRetard.title', { count: +count }) }}
                </template>
            </span>
            <span v-else-if="isFilterLoading && tableData.length > 0">
                <LoaderComponent/>
            </span>
            <ReglePastille cle="facturation.perimetreChantiers" />
            <ReglePastille cle="facturation.compteurFiltre" />
            <ReglePastille cle="facturation.defilementInfini" />
            <ReglePastille cle="facturation.triLocal" />
        </h2>
        <div @scroll="handleScroll" ref="content" class="datatable">
            <LoaderComponent v-if="isLoading && tableData.length === 0" />
            <DataTable
                v-else
                :count="+count"
                :tableHeader="tableHeader"
                :tableData="tableData || []"
                :isLoading="isLoading"
                :noDataText="$t('facture.enRetard.noData')"
                :stop-propa-on-pdf='true'
                @downloadPdf="downloadPdf"
                @select="downloadPdf"
                @sort="handleSort"
            />
        </div>
    </div>
</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import { SortType } from '@/models/SortType';
import { SuiviFacture, SuiviFactureEnRetard } from '@/models/SuiviFacture';
import { defineComponent, onMounted, ref } from 'vue';
import { SuiviFacturesEnRetard } from '../store/modules/SuiviFacturesEnRetard/action-types';
import { SUIVI_FACTURES_EN_RETARD } from '../store/modules/SuiviFacturesEnRetard/mutation-types';
import FilterSuiviFacture from '../composables/FilterSuiviFacture.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import { useSortDataClientSide } from '../composables/useSortDataClientSide';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'ListFacturesEnRetard',
    beforeMount() {
        this.getFacturesData();
    },
    watch: {
        '$route.query.chantierIds'() {
            this.getFacturesData();
        },
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    components: { DataTable, FilterSuiviFacture, LoaderComponent, ReglePastille },
    setup() {
        const content = ref<HTMLElement>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    data() {
        return {
            filter: {
                societe: '',
                etablissement: '',
                fournisseur: '',
                numeroFacture: '',
                dateFrom: '',
                dateTo: '',
                comparison: this.$t('facture.suivi.dropdown.novalue'),
                montant: null as number | null,
                joursRetard: null as number | null
            },
            totalCount: 0,
            isFiltered: false,
            isLoadingAll: false,
            isFilterLoading: false,
            comparisonList: [
                this.$t('facture.suivi.dropdown.novalue'),
                this.$t('facture.suivi.dropdown.lessthan'),
                this.$t('facture.suivi.dropdown.greaterthan'),
                this.$t('facture.suivi.dropdown.equal')
            ]
        };
    },
    computed: {
        tableData() {
            const items = this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardItems'];
            return Array.isArray(items) ? items.map(
                (f: SuiviFacture) => new SuiviFactureEnRetard(f)
            ) : [];
        },
        tableHeader() {
            return [
                {
                    label: this.$t('facture.enRetard.id'),
                    sortData: 'numeroFacture',
                    sortStatus: SortType.NONE,
                    width: '7%'
                },
                {
                    label: this.$t('facture.enRetard.company'),
                    sortData: 'raisonSociale',
                    sortStatus: SortType.NONE,
                    width: '10%'
                },
                {
                    label: this.$t('facture.enRetard.establishment'),
                    sortData: 'etablissementLibelle',
                    sortStatus: SortType.NONE,
                    width: '10%'
                },
                {
                    label: this.$t('facture.enRetard.site'),
                    sortData: 'site',
                    sortStatus: SortType.NONE,
                    width: '10%'
                },
                {
                    label: this.$t('facture.enRetard.supplier'),
                    sortData: 'fournisseur',
                    sortStatus: SortType.NONE,
                    width: '10%'
                },
                {
                    label: this.$t('facture.enRetard.date'),
                    sortData: 'dateFacture',
                    sortStatus: SortType.NONE,
                    width: '7%'
                },
                {
                    label: this.$t('facture.enRetard.montant'),
                    sortData: 'montantTtc',
                    sortStatus: SortType.NONE,
                    width: '14%',
                    textalign: 'end',
                },
                {
                    label: this.$t('facture.enRetard.solde'),
                    sortData: 'soldeTtc',
                    sortStatus: SortType.NONE,
                    width: '14%',
                    textalign: 'end',
                },
                {
                    label: this.$t('facture.enRetard.echeance'),
                    sortData: 'dateEcheance',
                    sortStatus: SortType.NONE,
                    width: '8%'
                },
                {
                    label: this.$t('facture.enRetard.retard'),
                    sortData: 'nbJourRetard',
                    sortStatus: SortType.NONE,
                    width: '5%'
                },
                {
                    label: '',
                    sortData: 'pdfExist',
                    sortStatus: SortType.INACTIVE,
                    width: '4%',
                    textalign: 'center',
                    regleCle: 'facturation.telechargementPdfConditionnel',
                },
            ];
        },
        page() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardPage'] || 0;
        },
        sort() {
            const sortObj = this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardSort'];
            if (!sortObj) return undefined;

            return sortObj.sortType === SortType.ASC
                ? sortObj.sortData
                : sortObj.sortType === SortType.DESC
                    ? `-${sortObj.sortData}`
                    : undefined;
        },
        count() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardCount'] || 0;
        },
        numberOfPages() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardNbOfPages'] || 0;
        },
        isLoading() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardIsLoading'] || false;
        },
        scroll() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardScroll'] || 0;
        },
    },
    methods: {
        handleSort(sortEvent: { sortData: string; sortStatus: SortType }) {
            // Update the tableHeader to show the correct sort icons
            this.tableHeader.forEach(header => {
                if (header.sortData === sortEvent.sortData) {
                    header.sortStatus = sortEvent.sortStatus;
                } else {
                    header.sortStatus = SortType.NONE;
                }
            });

            // Update the sort parameter in the store
            const sortParam = sortEvent.sortStatus === SortType.ASC
                ? sortEvent.sortData
                : sortEvent.sortStatus === SortType.DESC
                    ? `-${sortEvent.sortData}`
                    : undefined;

            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FACTURES_EN_RETARD.SET_SORT_EN_RETARD}`,
                sortParam ? { sortData: sortEvent.sortData, sortType: sortEvent.sortStatus } : undefined
            );

            // Sort the data client-side instead of reloading from the server
            this.sortDataClientSide(sortEvent.sortData, sortEvent.sortStatus);

            // Ensure the table is scrolled to the top
            this.content?.scrollTo({ top: 0 });
        },

        sortDataClientSide(sortData: string, sortStatus: SortType) {
            const { sortDataClientSide } = useSortDataClientSide();

            sortDataClientSide(
                this.$store,
                sortData,
                sortStatus,
                'suiviFacturesModuleState/getSuiviFacturesEnRetardItems',
                `suiviFacturesModuleState/${SUIVI_FACTURES_EN_RETARD.SET_ITEMS_EN_RETARD}`
            );
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FACTURES_EN_RETARD.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages && this.tableData.length < this.count) {
                this.getFacturesData(this.page + 1);
            }
        },
        getFacturesData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });

            // Check if any filters are applied
            this.isFiltered = !!(
                this.filter.societe ||
                this.filter.etablissement ||
                this.filter.fournisseur ||
                this.filter.numeroFacture ||
                this.filter.dateFrom ||
                this.filter.dateTo ||
                (this.filter.comparison !== this.$t('facture.suivi.dropdown.novalue') && this.filter.montant !== null) ||
                this.filter.joursRetard !== null
            );

            // Prepare filter parameters
            const params = {
                page,
                chantierIds: this.$route.query.chantierIds
                    ? String(this.$route.query.chantierIds).split(',').map(Number)
                    : [],
                sort: this.sort,
                societe: this.filter.societe,
                etablissement: this.filter.etablissement,
                fournisseur: this.filter.fournisseur,
                numeroFacture: this.filter.numeroFacture,
                dateFrom: this.filter.dateFrom,
                dateTo: this.filter.dateTo,
                comparison: this.filter.comparison !== this.$t('facture.suivi.dropdown.novalue') ? this.filter.comparison : undefined,
                montant: this.filter.montant,
                joursRetard: this.filter.joursRetard
            };

            return this.$store.dispatch(`suiviFacturesModuleState/${SuiviFacturesEnRetard.GET_SUIVI_FACTURES_EN_RETARD}`, params)
                .then(() => {
                    // Store the total count when first loading without filters
                    if (page === 0 && !this.isFiltered && this.count > 0) {
                        this.totalCount = this.count;
                    }
                });
        },
        downloadPdf(facture: SuiviFactureEnRetard) {
            const items = this.$store.getters['suiviFacturesModuleState/getSuiviFacturesEnRetardItems'];
            if (!Array.isArray(items)) return;

            const factureToDownload: SuiviFacture = items.find(
                (suiviFacture: SuiviFacture) => suiviFacture.numeroFacture === facture.numeroFacture
            );

            if(factureToDownload && factureToDownload.pdfExist){
                this.$store.dispatch(`suiviFacturesModuleState/${SuiviFacturesEnRetard.GET_FACTURES_EN_RETARD_PDF}`, factureToDownload);
            }
        },
        // Filter methods
        setSociete(value: string) {
            this.filter.societe = value;
        },
        setEtablissement(value: string) {
            this.filter.etablissement = value;
        },
        setFournisseur(value: string) {
            this.filter.fournisseur = value;
        },
        setNumeroFacture(value: string) {
            this.filter.numeroFacture = value;
        },
        setDateFrom(value: string) {
            this.filter.dateFrom = value;
        },
        setDateTo(value: string) {
            this.filter.dateTo = value;
        },
        setComparison(value: string) {
            this.filter.comparison = value;
        },
        setMontant(value: number | null) {
            this.filter.montant = value;
        },
        setJoursRetard(value: number | null) {
            this.filter.joursRetard = value;
        },
        async search() {
            // Reset sort status
            this.tableHeader.forEach(header => {
                if (header.sortStatus !== SortType.INACTIVE) {
                    header.sortStatus = SortType.NONE;
                }
            });

            // Reset sort in store
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FACTURES_EN_RETARD.SET_SORT_EN_RETARD}`, undefined);

            // Set flag to indicate we're loading filtered data
            this.isFilterLoading = true;

            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FACTURES_EN_RETARD.RESET_ITEMS}`);

            // Load first page to get total count and number of pages
            await this.getFacturesData(0);

            // Set flag to indicate we've finished loading filtered data
            this.isFilterLoading = false;

            // Ensure the table is scrolled to the top after data is loaded
            this.content?.scrollTo({ top: 0 });
        },
        clearFilter() {
            // Reset sort status
            this.tableHeader.forEach(header => {
                if (header.sortStatus !== SortType.INACTIVE) {
                    header.sortStatus = SortType.NONE;
                }
            });

            // Reset sort in store
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FACTURES_EN_RETARD.SET_SORT_EN_RETARD}`, undefined);

            this.filter = {
                societe: '',
                etablissement: '',
                fournisseur: '',
                numeroFacture: '',
                dateFrom: '',
                dateTo: '',
                comparison: this.$t('facture.suivi.dropdown.novalue'),
                montant: null as number | null,
                joursRetard: null as number | null
            };
            this.isFiltered = false;
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FACTURES_EN_RETARD.RESET_ITEMS}`);
            this.getFacturesData().then(() => {
                // Ensure the table is scrolled to the top after data is loaded
                this.content?.scrollTo({ top: 0 });
            });
        },
    },
});
</script>

<style lang="scss" scoped>
.filter-container {
    margin-bottom: 8px;
    background-color: var(--white);
    padding: 16px;
    border-radius: 6px;
}
.list-container {
    margin-bottom: 8px;
    background-color: var(--white);
    padding: 16px;
    border-radius: 6px;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, .2), 0 2px 2px rgba(0, 0, 0, .14), 0 1px 5px rgba(0, 0, 0, .12);
}
.title {
    color: var(--color-principal);
    margin-bottom: 16px;
    font-size: var(--font-size-medium);
    font-weight: 500;
}
@import '@/theme/pages-recherche.scss';

.datatable {
    max-height: calc(100vh - 410px);
    overflow-y: auto;
    position: relative;
}

</style>
